import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, Mail, Instagram } from 'lucide-react';

/**
 * BioSheet — bullet-proof bio modal for the host + trainer cards.
 *
 * Past issues this rewrite fixes:
 *   1. "Can't open this page" on iOS Chrome → no longer uses Radix
 *      Portal at all; renders via React's own createPortal into <body>.
 *   2. Black photo area on first open → image now `loading="eager"`
 *      with a controlled "loaded" fade so a half-decoded WebP can never
 *      look blank.
 *   3. Top of card cut off behind the site's fixed Navbar → mounted at
 *      document.body, so it sits OUTSIDE the App's isolated stacking
 *      context. z-index now beats the Navbar regardless of any future
 *      stacking-context changes.
 *   4. Hidden X button when modal exceeds viewport → X is now sticky
 *      AND there's a secondary full-width "CLOSE BIO" button at the
 *      end of the body so a close target is always reachable.
 *   5. iOS browser BACK leaving the site → opening the modal pushes a
 *      history entry; the next popstate (BACK / swipe-back) closes the
 *      modal instead of leaving the page.
 *   6. Modal taller than dynamic viewport → uses `100dvh` so iOS
 *      Safari/Chrome's collapsing URL bar can't clip the card.
 */
export default function BioSheet({ open, onOpenChange, person }) {
  // Render-state: stay mounted through the close-out fade.
  const [render, setRender] = useState(open);
  const [closing, setClosing] = useState(false);
  const closeTimerRef = useRef(null);

  // Cache last-non-null person so the fade-out keeps showing real content.
  const lastPersonRef = useRef(person);
  useEffect(() => { if (person) lastPersonRef.current = person; }, [person]);
  const active = person || lastPersonRef.current;

  // open=true → render now; open=false → fade out then unmount.
  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
      setClosing(false);
      setRender(true);
    } else if (render) {
      setClosing(true);
      closeTimerRef.current = setTimeout(() => {
        setRender(false);
        setClosing(false);
        closeTimerRef.current = null;
      }, 220);
    }
    return () => {
      if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    };
  }, [open, render]);

  // Scroll lock + ESC handler while open.
  //
  // We tried the "position: fixed + top: -scrollY" pattern previously but
  // it produced a real bug in practice: browsers don't reliably relayout
  // the page in the same microtask as the style un-set, so the
  // subsequent `window.scrollTo(0, scrollY)` got clamped to 0 (the
  // document was still effectively single-viewport-tall at that
  // instant). Users landed at the top of the page after closing the
  // bio — exactly the regression the user reported.
  //
  // The simpler and more robust pattern is `overflow: hidden` on BOTH
  // <html> and <body>. The body never leaves the document flow, so the
  // scroll position never moves in the first place — no restoration
  // needed. On iOS Safari/Chrome we also pin `touch-action: none` to
  // prevent rubber-band scrolling of the page underneath the modal.
  useEffect(() => {
    if (!render) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onOpenChange(false); };
    document.addEventListener('keydown', onKey);

    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyTouchAction: body.style.touchAction,
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';

    return () => {
      document.removeEventListener('keydown', onKey);
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.touchAction = prev.bodyTouchAction;
    };
  }, [render, onOpenChange]);

  // Browser-back handler — push a history entry on open, listen for
  // popstate (iOS swipe-back, Chrome BACK button) to close the modal
  // instead of leaving the page. Idempotent — only pushes one entry.
  const historyPushedRef = useRef(false);
  useEffect(() => {
    if (!render) return undefined;
    if (!historyPushedRef.current) {
      try {
        window.history.pushState({ bio: true }, '', window.location.href);
        historyPushedRef.current = true;
      } catch (_err) { /* private mode / SSR — silently ignore */ }
    }
    const onPop = () => {
      historyPushedRef.current = false;
      onOpenChange(false);
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Clean up the history entry when modal closes normally (X / backdrop)
      if (historyPushedRef.current) {
        historyPushedRef.current = false;
        try { window.history.back(); } catch (_err) { /* ignore */ }
      }
    };
  }, [render, onOpenChange]);

  const requestClose = useCallback((e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    onOpenChange(false);
  }, [onOpenChange]);

  // Image-loaded state so the photo fades in instead of flashing black.
  const [imgLoaded, setImgLoaded] = useState(false);
  useEffect(() => { if (active) setImgLoaded(false); }, [active && active.src]);

  if (!render || !active || typeof document === 'undefined') return null;

  // Build phone/email/instagram only when the payload is valid.
  const phoneDigits = active.phone ? active.phone.replace(/[^\d+]/g, '') : '';
  const phoneHref = phoneDigits ? `tel:${phoneDigits}` : null;
  const emailHref = active.email && active.email.includes('@') ? `mailto:${active.email}` : null;
  const instaHref = active.instagram ? `https://instagram.com/${active.instagram.replace(/^@/, '')}` : null;

  const modal = (
    <div
      data-testid="bio-sheet"
      aria-modal="true"
      role="dialog"
      aria-label={`${active.first} ${active.last} — bio`}
      className="bio-sheet-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 100dvh tracks the actual visible viewport on iOS — when the
        // URL bar collapses, the modal expands; when it returns, the
        // modal contracts. Prevents top/bottom clipping in all browser
        // chrome states.
        height: '100dvh',
        width: '100vw',
      }}
    >
      {/* Backdrop — tap to close */}
      <div
        onClick={requestClose}
        className={closing ? 'bio-fade-out' : 'bio-fade-in'}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.88)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
        }}
      />

      {/* Card */}
      <div
        className={closing ? 'bio-pop-out' : 'bio-pop-in'}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'calc(100vw - 1.5rem)',
          maxWidth: 440,
          maxHeight: 'min(88dvh, 760px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 28,
          border: '3px solid rgba(239,43,43,0.65)',
          background: '#0a0a0a',
          boxShadow: '0 30px 90px rgba(239,43,43,0.45)',
        }}
      >
        {/* === HERO PORTRAIT === */}
        <div
          style={{
            position: 'relative',
            width: '100%',
            aspectRatio: '5 / 4',
            background: '#18181b',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <img
            src={active.src}
            alt={`${active.first} ${active.last}`}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgLoaded(true)}
            loading="lazy"
            decoding="async"
            fetchPriority="high"
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              objectPosition: 'center 18%',
              opacity: imgLoaded ? 1 : 0,
              transition: 'opacity 220ms ease-out',
            }}
          />
          {/* Skeleton shimmer while the image decodes */}
          {!imgLoaded && (
            <div
              aria-hidden
              style={{
                position: 'absolute', inset: 0,
                background:
                  'linear-gradient(110deg, #18181b 30%, #27272a 50%, #18181b 70%)',
                backgroundSize: '200% 100%',
                animation: 'bio-skeleton-shimmer 1.4s linear infinite',
              }}
            />
          )}
          <div
            aria-hidden
            style={{
              position: 'absolute', inset: 0,
              background: 'linear-gradient(to top, #000 0%, rgba(0,0,0,0.3) 50%, transparent 100%)',
              pointerEvents: 'none',
            }}
          />

          {/* TOP row — badge + close. Sticky so even if anything ever
              causes the card to scroll, the X stays reachable. */}
          <div
            style={{
              position: 'absolute',
              top: 12, left: 12, right: 12,
              zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <div
              style={{
                padding: '4px 10px',
                borderRadius: 9999,
                background: '#ef2b2b',
                boxShadow: '0 4px 18px rgba(239,43,43,0.5)',
              }}
            >
              <span style={{ display: 'block', fontSize: 9, letterSpacing: '0.25em', fontWeight: 800, color: '#fff', whiteSpace: 'nowrap' }}>
                {active.badge}
              </span>
            </div>
            <button
              type="button"
              onClick={requestClose}
              onPointerUp={(e) => { e.stopPropagation(); }}
              data-testid="bio-sheet-close"
              aria-label="Close bio"
              style={{
                flexShrink: 0,
                height: 44, width: 44,
                borderRadius: 9999,
                background: 'rgba(0,0,0,0.85)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                border: '1.5px solid rgba(239,43,43,0.7)',
                color: '#fff',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>

          {/* Name plate */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0 16px 14px', zIndex: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 9, letterSpacing: '0.35em', color: '#fca5a5', fontWeight: 800, marginBottom: 6 }}>
              <div style={{ height: 1.5, width: 18, background: '#ef4444', flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {active.role.toUpperCase()} · {active.location || 'GALT · CA'}
              </span>
            </div>
            <div
              className="font-display"
              style={{
                fontSize: 28, lineHeight: 1, color: '#fff',
                textShadow: '0 4px 20px rgba(0,0,0,0.95)',
              }}
            >
              {active.first} <span style={{ color: '#ef4444' }}>{active.last}</span>
            </div>
          </div>
        </div>

        {/* === SCROLLABLE BODY === */}
        <div
          style={{
            overflowY: 'auto',
            padding: '20px 22px 24px',
            WebkitOverflowScrolling: 'touch',
            flex: 1,
            minHeight: 0,
          }}
        >
          {active.summary && (
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.9)', lineHeight: 1.6, marginBottom: 16, fontWeight: 500 }}>
              {active.summary}
            </p>
          )}
          {active.bio && (
            <p style={{ fontSize: 12.5, color: '#a1a1aa', lineHeight: 1.65, marginBottom: 20 }}>
              {active.bio}
            </p>
          )}

          {/* Contact rows */}
          {(phoneHref || emailHref || instaHref) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 14, borderTop: '1px solid #27272a' }}>
              {phoneHref && (
                <a href={phoneHref} data-testid="bio-phone" style={contactRowStyle}>
                  <Phone size={14} color="#ef4444" />
                  <span>{active.phone}</span>
                </a>
              )}
              {emailHref && (
                <a href={emailHref} data-testid="bio-email" style={{ ...contactRowStyle, wordBreak: 'break-all' }}>
                  <Mail size={14} color="#ef4444" />
                  <span>{active.email}</span>
                </a>
              )}
              {instaHref && (
                <a href={instaHref} target="_blank" rel="noopener noreferrer" data-testid="bio-instagram" style={contactRowStyle}>
                  <Instagram size={14} color="#ef4444" />
                  <span>@{active.instagram.replace(/^@/, '')}</span>
                </a>
              )}
            </div>
          )}

          {/* Fallback close — always reachable even if user scrolls the
              body and somehow loses sight of the top X. Full width so
              it's impossible to miss on mobile. */}
          <button
            type="button"
            onClick={requestClose}
            data-testid="bio-sheet-close-bottom"
            style={{
              marginTop: 22,
              width: '100%',
              padding: '14px 16px',
              borderRadius: 12,
              background: '#ef2b2b',
              color: '#fff',
              fontWeight: 800,
              fontSize: 11,
              letterSpacing: '0.3em',
              cursor: 'pointer',
              border: 'none',
              boxShadow: '0 6px 22px rgba(239,43,43,0.45)',
            }}
          >
            CLOSE BIO
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

const contactRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  fontSize: 12,
  letterSpacing: '0.12em',
  color: '#d4d4d8',
  textDecoration: 'none',
};
